import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ScheduleEntryStatus, ScheduleEntryStatusMap } from "./DetailedItem";
import {
  getStylistDayAvailability,
  isZeroDurationEvent,
} from "./availabilityUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const MIN_OPEN_MINUTES = 15;

function onSameDay(value, selectedDate, timeZone) {
  if (!value) return false;
  return (
    dayjs(value).tz(timeZone).format("YYYY-MM-DD") ===
    dayjs(selectedDate).tz(timeZone).format("YYYY-MM-DD")
  );
}

function atWallTime(selectedDate, timeStr, timeZone) {
  if (!timeStr) return null;
  const day = dayjs(selectedDate).tz(timeZone).format("YYYY-MM-DD");
  const clock = String(timeStr).slice(0, 8);
  const parsed = dayjs.tz(`${day} ${clock}`, timeZone);
  return parsed.isValid() ? parsed : null;
}

export function formatSheetRange(start, end, timeZone) {
  const s = dayjs(start).tz(timeZone);
  const e = dayjs(end).tz(timeZone);
  if (s.format("A") === e.format("A")) {
    return `${s.format("h:mm")} – ${e.format("h:mm A")}`;
  }
  return `${s.format("h:mm A")} – ${e.format("h:mm A")}`;
}

export function formatSheetDuration(minutes) {
  const total = Math.max(0, Math.round(minutes));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours && mins) return `${hours} hr ${mins} min`;
  if (hours) return hours === 1 ? "1 hr" : `${hours} hr`;
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

function statusText(event) {
  const base =
    ScheduleEntryStatusMap[event.appointment_status] || "Scheduled";
  const flags = [];
  if (event.is_late) flags.push("Late");
  if (event.isNewToSalon) flags.push("New to salon");
  if (event.isNewToStylist) flags.push("New to stylist");
  if (event.isClientConfirmed) flags.push("Confirmed");
  return flags.length ? `${base} · ${flags.join(" · ")}` : base;
}

function serviceText(event) {
  const titles = (event.services || [])
    .map((service) => service.title || service.service_name)
    .filter(Boolean);
  if (titles.length) return titles.join(", ");
  return "";
}

function mergeBookedMinutes(blocks) {
  const sorted = [...blocks].sort((a, b) => a.start.valueOf() - b.start.valueOf());
  let total = 0;
  let cursor = null;
  sorted.forEach((block) => {
    const start = block.start.valueOf();
    const end = block.end.valueOf();
    if (cursor == null || start >= cursor) {
      total += end - start;
      cursor = end;
      return;
    }
    if (end > cursor) {
      total += end - cursor;
      cursor = end;
    }
  });
  return Math.round(total / 60000);
}

/**
 * Chronological day sheet for one stylist: appointment blocks plus open gaps
 * of at least 15 minutes inside the stylist's shift.
 */
export function buildStylistDaySheet({
  stylist,
  events = [],
  selectedDate,
  timeZone,
  includeCanceled = false,
  locationName = "",
  salonAddress = "",
}) {
  const date = dayjs(selectedDate).tz(timeZone);
  const availability = stylist ? getStylistDayAvailability(stylist, date) : null;
  const shiftStart = availability
    ? atWallTime(date, availability.start_time, timeZone)
    : null;
  const shiftEnd = availability
    ? atWallTime(date, availability.end_time, timeZone)
    : null;

  const blocks = (events || [])
    .filter((event) => event && event.user_id === stylist?.user_id)
    .filter((event) => onSameDay(event.start, date, timeZone))
    .filter((event) => !isZeroDurationEvent(event))
    .filter((event) => {
      if (includeCanceled) return true;
      return event.appointment_status !== ScheduleEntryStatus.Canceled;
    })
    .map((event) => ({
      start: dayjs(event.start).tz(timeZone),
      end: dayjs(event.end).tz(timeZone),
      client: event.client?.client_name || event.title || "Walk-in",
      service: serviceText(event),
      status: statusText(event),
    }))
    .filter((block) => block.end.isAfter(block.start))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf() || a.end.valueOf() - b.end.valueOf());

  const rows = [];
  const pushOpen = (start, end) => {
    if (!start || !end || end.diff(start, "minute") < MIN_OPEN_MINUTES) return;
    rows.push({
      kind: "open",
      timeLabel: formatSheetRange(start, end, timeZone),
      client: "Open",
      service: "",
      status: "",
    });
  };
  const pushAppointment = (block) => {
    rows.push({
      kind: "appointment",
      timeLabel: formatSheetRange(block.start, block.end, timeZone),
      client: block.client,
      service: block.service,
      status: block.status,
    });
  };

  if (!blocks.length && shiftStart && shiftEnd) {
    pushOpen(shiftStart, shiftEnd);
  } else {
    let cursor = shiftStart;
    blocks.forEach((block) => {
      if (cursor && block.start.isAfter(cursor)) {
        pushOpen(cursor, block.start);
      }
      pushAppointment(block);
      if (!cursor || block.end.isAfter(cursor)) {
        cursor = block.end;
      }
    });
    if (cursor && shiftEnd && shiftEnd.isAfter(cursor)) {
      pushOpen(cursor, shiftEnd);
    }
  }

  const bookedMinutes = mergeBookedMinutes(blocks);
  let openMinutes = 0;
  if (shiftStart && shiftEnd) {
    const shiftMinutes = Math.max(0, shiftEnd.diff(shiftStart, "minute"));
    const bookedInside = blocks.reduce((sum, block) => {
      const start = block.start.isBefore(shiftStart) ? shiftStart : block.start;
      const end = block.end.isAfter(shiftEnd) ? shiftEnd : block.end;
      const minutes = end.diff(start, "minute");
      return sum + (minutes > 0 ? minutes : 0);
    }, 0);
    openMinutes = Math.max(0, shiftMinutes - bookedInside);
  }

  const shiftLabel =
    shiftStart && shiftEnd
      ? formatSheetRange(shiftStart, shiftEnd, timeZone)
      : "";

  return {
    stylistName: stylist?.user_name || "",
    dateLabel: date.format("dddd, MMMM D, YYYY"),
    dateStamp: date.format("YYYY-MM-DD"),
    shiftLabel,
    locationName: locationName || "",
    salonAddress: salonAddress || "",
    rows,
    appointmentCount: blocks.length,
    bookedLabel: formatSheetDuration(bookedMinutes),
    openLabel: shiftStart && shiftEnd ? formatSheetDuration(openMinutes) : "",
  };
}

export function stylistDaySheetFileName(sheet) {
  const slug = (sheet?.stylistName || "stylist")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `schedule-${slug || "stylist"}-${sheet?.dateStamp || "day"}.pdf`;
}
